import { useState, useEffect, useRef } from "react";

const codeTabs = ["cURL", "Node.js", "Python", "PHP"];
const codeSnippets: Record<string, string> = {
  "cURL": `curl https://api.paygent.com/v1/payments \\
  -H "Authorization: Bearer pgt_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d "{
    \\"amount\\": 25000,
    \\"currency\\": \\"USD\\",
    \\"source\\": \\"tok_visa\\",
    \\"description\\": \\"Order #1234\\"
  }"`,
  "Node.js": `const paygent = require('paygent')(process.env.KEY);
const payment = await paygent.payments.create({
  amount: 25000,
  currency: 'USD',
  source: 'tok_visa',
  description: 'Order #1234',
});`,
  "Python": `import paygent
paygent.api_key = 'pgt_live_xxx'
payment = paygent.Payment.create(
  amount=25000,
  currency='usd',
  source='tok_visa',
  description='Order #1234'
)`,
  "PHP": `$paygent = new \\Paygent\\PaygentClient('pgt_live_xxx');
$payment = $paygent->payments->create([
  'amount' => 25000,
  'currency' => 'usd',
  'source' => 'tok_visa',
  'description' => 'Order #1234',
]);`,
};

const payoutItems = [
  { flag: "🇺🇸", name: "Bank Transfer", status: "Paid", amount: "$12,500.00" },
  { flag: "🇬🇧", name: "Faster Payments", status: "Paid", amount: "£8,230.00" },
  { flag: "🇪🇺", name: "SEPA Transfer", status: "Paid", amount: "€9,850.00" },
  { flag: "🇨🇦", name: "e-Transfer", status: "Paid", amount: "$6,420.00" },
];

function RadarVisual() {
  return (
    <div className="relative flex items-center justify-center w-full mt-4">
      <div className="relative w-48 h-48 md:w-56 md:h-56">
        {[100, 75, 50, 28].map((size, i) => (
          <div
            key={i}
            className="absolute rounded-full border"
            style={{
              width: `${size}%`,
              height: `${size}%`,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              borderColor: `rgba(37,99,235,${0.08 + i * 0.04})`,
              background: i === 3 ? "rgba(37,99,235,0.06)" : "transparent",
            }}
          />
        ))}
        {/* Rotating scan line */}
        <div className="absolute inset-0 rounded-full overflow-hidden" style={{ animation: "radar-spin 4s linear infinite" }}>
          <div className="absolute top-1/2 left-1/2 h-1/2 w-[2px] origin-bottom"
            style={{
              background: "linear-gradient(to top, transparent, rgba(37,99,235,0.6))",
              transformOrigin: "bottom center",
            }}
          />
        </div>
        {/* Shield icon center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full"
          style={{ background: "linear-gradient(135deg, #2563EB, #14B8A6)", boxShadow: "0 0 20px rgba(37,99,235,0.3)" }}>
          <i className="ri-shield-check-line text-white text-xl w-6 h-6 flex items-center justify-center" />
        </div>
        {/* Dots */}
        {[
          { top: "15%", left: "60%", color: "#2563EB" },
          { top: "65%", left: "20%", color: "#14B8A6" },
          { top: "70%", left: "72%", color: "#EF4444" },
          { top: "30%", left: "80%", color: "#2563EB" },
        ].map((dot, i) => (
          <div key={i} className="absolute w-2.5 h-2.5 rounded-full"
            style={{ top: dot.top, left: dot.left, background: dot.color, boxShadow: `0 0 6px ${dot.color}80`, animation: `pulse-dot ${1.5 + i * 0.4}s ease-in-out infinite` }} />
        ))}
      </div>
    </div>
  );
}

function ChartVisual() {
  const ref = useRef<SVGSVGElement>(null);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setDrawn(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const points = [
    [0, 100], [60, 85], [120, 90], [180, 60], [240, 40], [300, 20], [360, 5],
  ];
  const pathD = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  const fillD = pathD + ` L 360 100 L 0 100 Z`;

  return (
    <div className="mt-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs text-gray-400 mb-0.5">Savings this month</div>
          <div className="text-2xl font-bold text-gray-900">$24,340.18</div>
        </div>
        <div className="px-2.5 py-1 rounded-full text-xs font-bold text-green-600 bg-green-50">+18.6%</div>
      </div>
      <svg ref={ref} viewBox="0 0 360 110" className="w-full h-24" preserveAspectRatio="none">
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </linearGradient>
          <clipPath id="chartClip">
            <rect x="0" y="0" width={drawn ? "360" : "0"} height="110" style={{ transition: "width 2s ease-out" }} />
          </clipPath>
        </defs>
        <path d={fillD} fill="url(#chartGrad)" clipPath="url(#chartClip)" />
        <path d={pathD} fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" clipPath="url(#chartClip)" />
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="4" fill="white" stroke="#2563EB" strokeWidth="2" clipPath="url(#chartClip)" />
        ))}
      </svg>
    </div>
  );
}

export default function Features() {
  const [activeTab, setActiveTab] = useState("cURL");

  return (
    <section id="features-section" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Built for modern platforms</h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">Everything you need to accept payments, prevent fraud, and scale globally.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Card 1: Developers First */}
          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg"
                style={{ background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" }}>
                <i className="ri-code-s-slash-line text-blue-600 text-sm w-4 h-4 flex items-center justify-center" />
              </div>
              <span className="text-sm font-semibold text-gray-600">Developers First</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">Powerful APIs.<br />Built for builders.</h3>
            <p className="text-gray-500 text-sm mb-5 leading-relaxed">Accept payments, create customers, send payouts, and automate everything.</p>

            {/* Code block */}
            <div className="rounded-xl overflow-hidden" style={{ background: "#0F172A" }}>
              <div className="flex items-center gap-1 px-4 py-2 border-b border-white/10">
                {codeTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === tab ? "text-white bg-white/15" : "text-gray-400 hover:text-gray-200"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="p-4 overflow-x-auto">
                <pre className="text-xs leading-relaxed">
                  {codeSnippets[activeTab].split("\n").map((line, i) => {
                    const lineNum = i + 1;
                    const colored = line
                      .replace(/(https?:\/\/[^\s"\\]+)/g, '<span style="color:#93C5FD">$1</span>')
                      .replace(/("(?:[^"\\]|\\.)*")/g, '<span style="color:#86EFAC">$1</span>')
                      .replace(/(\d+)/g, '<span style="color:#FCA5A5">$1</span>');
                    return (
                      <div key={i} className="flex gap-3">
                        <span className="text-gray-600 select-none w-4 text-right flex-shrink-0">{lineNum}</span>
                        <span className="text-gray-300" dangerouslySetInnerHTML={{ __html: colored }} />
                      </div>
                    );
                  })}
                </pre>
              </div>
              <div className="px-4 py-2 border-t border-white/10 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <span className="text-xs text-gray-400">200 OK</span>
                <span className="text-xs text-gray-500">• 482ms</span>
              </div>
            </div>
            <a href="/developers/docs" className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
              View docs <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </a>
          </div>

          {/* Card 2: Level III Data */}
          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg"
                style={{ background: "linear-gradient(135deg, #F0FDFA, #CCFBF1)" }}>
                <i className="ri-bar-chart-grouped-line text-teal-600 text-sm w-4 h-4 flex items-center justify-center" />
              </div>
              <span className="text-sm font-semibold text-gray-600">Level III Data</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">More data. Lower fees.<br />Higher approval rates.</h3>
            <p className="text-gray-500 text-sm mb-4 leading-relaxed">Unlock the power of Level III processing and reduce interchange costs.</p>
            <a href="/resources/guides" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
              Explore Level III <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </a>
            <ChartVisual />
          </div>

          {/* Card 3: AI Fraud Protection */}
          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg"
                style={{ background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" }}>
                <i className="ri-shield-flash-line text-blue-600 text-sm w-4 h-4 flex items-center justify-center" />
              </div>
              <span className="text-sm font-semibold text-gray-600">AI Fraud Protection</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">Intelligent protection<br />that adapts in real-time.</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Our AI models analyze millions of data points to stop fraud before it happens.</p>
            <RadarVisual />
            <a href="/products/radar" className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
              Explore Radar <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </a>
          </div>

          {/* Card 4: Global Payouts */}
          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg"
                style={{ background: "linear-gradient(135deg, #F0FDFA, #CCFBF1)" }}>
                <i className="ri-global-line text-teal-600 text-sm w-4 h-4 flex items-center justify-center" />
              </div>
              <span className="text-sm font-semibold text-gray-600">Global Payouts</span>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">Payouts that move as fast as your business.</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">Send funds globally with local rails, real-time tracking, and automated reconciliation.</p>
                <a href="/products/billing" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
                  View Payouts <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </a>
              </div>
              <div className="flex-1">
                <div className="space-y-2 mb-3">
                  {payoutItems.map((item) => (
                    <div key={item.name} className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-gray-50">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{item.flag}</span>
                        <span className="text-sm font-medium text-gray-700">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Paid</span>
                        <span className="text-sm font-semibold text-gray-800">{item.amount}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="w-full py-2.5 text-sm font-semibold text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap">
                  View all payouts
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes radar-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.7); }
        }
      `}</style>
    </section>
  );
}
